import { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

const WHATSAPP_NUMBER = "523921003836";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Por favor completa nombre y teléfono.");
      return;
    }
    setSubmitting(true);
    const text = encodeURIComponent(
      `Hola Real San José, soy ${form.name}.\nTeléfono: ${form.phone}\nCorreo: ${form.email || "—"}\nMensaje: ${form.message || "Quiero información sobre los lotes."}`
    );
    setTimeout(() => {
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, "_blank");
      toast.success("Te conectamos con un asesor por WhatsApp.");
      setSubmitting(false);
      setForm({ name: "", email: "", phone: "", message: "" });
    }, 500);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#3E482A] text-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <span className="text-xs tracking-[0.3em] uppercase text-[#C9B68A] font-medium">
              Contacto
            </span>
            <h2 className="font-heading mt-4 text-4xl sm:text-5xl font-light tracking-tight leading-[1.05]">
              Comienza tu
              <br />
              <em className="italic font-extralight text-[#C9B68A]">proyecto</em>.
            </h2>
            <p className="mt-6 text-white/80 font-light leading-relaxed text-lg max-w-md">
              Déjanos tus datos y un asesor especializado se comunicará contigo
              para resolver tus dudas y agendar una visita al desarrollo.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-phone"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center group-hover:bg-[#C9B68A] group-hover:border-[#C9B68A] transition-colors">
                  <Phone size={18} strokeWidth={1.6} />
                </div>
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-white/60">Teléfono</div>
                  <div className="font-heading text-xl font-light">392 100 3836</div>
                </div>
              </a>

              <a
                href="mailto:realsanjose@lauburu.mx"
                data-testid="contact-email"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center group-hover:bg-[#C9B68A] group-hover:border-[#C9B68A] transition-colors">
                  <Mail size={18} strokeWidth={1.6} />
                </div>
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-white/60">Correo</div>
                  <div className="font-heading text-xl font-light">realsanjose@lauburu.mx</div>
                </div>
              </a>

              <div className="flex items-center gap-4" data-testid="contact-address">
                <div className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center">
                  <MapPin size={18} strokeWidth={1.6} />
                </div>
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-white/60">Oficinas</div>
                  <div className="font-heading text-xl font-light">Visitas con cita previa</div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white text-[#2D2920] p-8 md:p-12 rounded-md"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs tracking-[0.2em] uppercase text-[#7a6f55]">
                    Nombre
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Tu nombre"
                    data-testid="contact-input-name"
                    className="border-[#DDD8CC] focus-visible:ring-[#3E482A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs tracking-[0.2em] uppercase text-[#7a6f55]">
                    Teléfono
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+52 ..."
                    data-testid="contact-input-phone"
                    className="border-[#DDD8CC] focus-visible:ring-[#3E482A]"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase text-[#7a6f55]">
                    Correo
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="tu@correo.com"
                    data-testid="contact-input-email"
                    className="border-[#DDD8CC] focus-visible:ring-[#3E482A]"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="message" className="text-xs tracking-[0.2em] uppercase text-[#7a6f55]">
                    Mensaje
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    placeholder="Cuéntanos qué tipo de lote te interesa..."
                    rows={5}
                    data-testid="contact-input-message"
                    className="border-[#DDD8CC] focus-visible:ring-[#3E482A]"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="group mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 bg-[#3E482A] text-white text-xs font-medium tracking-[0.2em] uppercase hover:bg-[#2D2920] disabled:opacity-60 transition-colors rounded-md"
              >
                {submitting ? "Enviando..." : "Enviar mensaje"}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <p className="mt-4 text-xs text-[#7a6f55]">
                Al enviar, te conectamos directamente con un asesor por WhatsApp.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
